import { Lock, LockOpen, Star } from '@mui/icons-material';
import React from 'react';
import { theme } from '../../../../../constants/theme';
import { useConfig } from '../../../../../context/ConfigContext';
import { LoyaltyArticle } from '../../../../../interfaces/loyality.interface';
import './LoyaltyArticleCard.scss';
import ImageLazyLoader from '../../../../common/ImageLazyLoader';

interface LoyaltyArticleCardProps {
  item: LoyaltyArticle;
  onPress: () => void;
  discountMessage?: string;
  itemsPerRow?: 1 | 2 | 3 | 4;
  containerPadding?: number;
  itemSpacing?: number;
}

export const LoyaltyArticleCard: React.FC<LoyaltyArticleCardProps> = ({
  item,
  onPress,
  discountMessage,
  itemsPerRow = 2,
  containerPadding = 16,
  itemSpacing = 12,
}) => {
  const { config } = useConfig();
  const primaryColor: string = (config?.primaryColor as string) || String(theme.colors.primary);

  const calculateItemWidth = () => {
    // For web, we'll use CSS Grid instead of manual calculations
    // This is just for reference or specific width requirements
    const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 375;
    const totalPadding = containerPadding * 2;
    const totalSpacing = (itemsPerRow - 1) * itemSpacing;
    const availableWidth = screenWidth - totalPadding - totalSpacing;

    if (itemsPerRow === 3) {
      return availableWidth / 2.5;
    }

    return availableWidth / itemsPerRow;
  };

  const itemWidth = calculateItemWidth();
  const imageHeight = itemsPerRow === 3 ? 120 : 100;
  const fontSize = itemsPerRow === 3 ? 13 : 16;

  const cardClasses = [
    'loyalty-article-card',
    item.isLocked ? 'locked' : 'unlocked',
    `items-per-row-${itemsPerRow}`
  ].filter(Boolean).join(' ');

  return (
    <div
      className={cardClasses}
      onClick={onPress}
      style={{
        '--primary-color': primaryColor,
        '--image-height': `${imageHeight}px`,
        '--font-size': `${fontSize}px`,
        '--item-width': `${itemWidth}px`,
        '--item-spacing': `${itemSpacing}px`,
      } as React.CSSProperties}
    >
      {/* Status Badge */}
      <div
        className="status-badge"
        style={{ backgroundColor: item.isLocked ? '#94A3B8' : primaryColor }}
      >
        {item.isLocked ? (
          <Lock className="status-icon" />
        ) : (
          <LockOpen className="status-icon" />
        )}
      </div>

      {/* Points Badge */}
      <div className="points-badge">
        <span
          className="points-badge-text"
          style={{ color: primaryColor }}
        >
          {item.pointsRequired}
        </span>
        <Star
          className="star-icon"
          style={{ color: primaryColor }}
        />
      </div>

      {/* Reward Image */}
      <div
        className="reward-image-container"
        style={{ height: imageHeight }}
      >
        <ImageLazyLoader
          src={item.image || config?.appLogoImage || '/placeholder-image.png'}
          alt={item.name}
          effect="blur"
          className="reward-image"
        />
      </div>

      {/* Reward Name */}
      <div className={`reward-name ${item.isLocked ? 'locked-text' : ''}`}>
        {item.name}
      </div>

      {/* Discount Message */}
      <div
        className="discount-message"
        style={{
          backgroundColor: item.isLocked ? '#E2E8F0' : `${primaryColor}15`,
          fontSize: itemsPerRow === 3 ? '11px' : '12px'
        }}
      >
        <span
          className="discount-message-text"
          style={{ color: item.isLocked ? '#64748B' : primaryColor }}
        >
          {discountMessage || `Réduction ${Math.round(item.discountPercent)}%`}
        </span>
      </div>
    </div>
  );
};